import React, { useState } from 'react';
import { usePortal } from '../../contexts/PortalContext';
import type { Project } from '../../types/portal';
import { projectStatus } from '../../utils/status';
import { BUTTON_QUIET, BUTTON_SOLID, FIELD } from './adminStyles';

/**
 * **THE SHOOT FIELDS ARE READ-ONLY. 17 Oct 2026.**
 *
 * Next Shoot Date, Time and Location were three editable inputs writing to
 * `projects.next_shoot_date / next_shoot_time / shoot_location` — which are
 * a DERIVED MIRROR, not storage. Confirmed in the database:
 * `project_shoots_sync` fires AFTER INSERT, UPDATE *and DELETE* on
 * `project_shoots` and calls `refresh_project_next_shoot`, which overwrites
 * all three from the chosen shoot row — or sets them NULL when the last
 * row is deleted.
 *
 * So anything typed here survived only until somebody next touched a shoot
 * day. A form that accepts work and throws it away later is worse than no
 * form. They now show what they are: the next day, read off the shoot days.
 * **Shoot days are edited on the Calendar.** Do not put editable copies back.
 *
 * **THE CALL SHEET PDF UPLOAD IS GONE. 17 Oct 2026.**
 *
 * There was a *Call sheet PDF (optional)* field here that stored a file in
 * the public `call-sheets` bucket. It was the second of two things in the
 * OS called a call sheet, and the lesser one — a flat attachment against
 * the Call Sheets tab, which builds real sheets with crew, a schedule,
 * shots, kit and their own crew and client links.
 *
 * It was also already dead. The only thing that ever rendered it was the
 * *View Call Sheet* button on pages/client/Schedule.tsx, and ClientApp
 * folds the `schedule` route (with home, project, episodes and files) into
 * ProjectPage — so no client could reach it. Nothing displayed what this
 * field uploaded.
 *
 * Tom set the rule that made the decision easy: *"The call sheet is
 * generated from that tab and a link is sent directly to either a whatsapp
 * group or emailed directly. It is different from the client page."* One
 * place makes call sheets.
 *
 * `projects.call_sheet` and the stored files are untouched — removing the
 * field does not delete anybody's data, and `save()` no longer sends the
 * key, so an existing value simply stays where it is. utils/callSheetStore.ts
 * is now unreferenced and can go whenever the dead files are swept.
 *
 * TWO FIELDS THAT LOOK LIKE DUPLICATES AND ARE NOT — relabelled rather than
 * removed, because each has a real consumer:
 *
 *   • The client contact is what a CALL SHEET prints as the client's person
 *     on the day (`client_contact`, and `client_contact_phone`, which has a
 *     column but still no field here). It is not the "who to email when a
 *     cut goes up" list — that is Telling The Client, and those are rows in
 *     `project_client_contacts` with real addresses.
 *   • The description is the studio's own note. The line the CLIENT reads
 *     is the outline, written in setup. Two fields, two audiences.
 */

/** A small heading over a cluster of related fields. */
function FieldGroup({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <div className="mt-10 first:mt-0" data-id="element-1169" data-mp="3ryy1hdx1v0z2">
      <span className="label block text-paper/25" data-id="element-1170" data-mp="9jdoirull0fa">{label}</span>
      <div className="mt-5 grid grid-cols-1 gap-7 sm:grid-cols-2" data-id="element-1171" data-mp="9kyj2hgwwjd5">{children}</div>
    </div>;
}

/** A value the project owns but this form must not write. */
function ReadOnly({
  label,
  value,
  note
}: {
  label: string;
  value: string;
  note?: string;
}) {
  return <div className="block" data-id="element-1172" data-mp="ej9tatb4cvvw">
      <span className="label block text-paper/40" data-id="element-1173" data-mp="ehyqowtetu6d">{label}</span>
      <p className="mt-3 border-b border-paper/25 pb-2 font-sans text-sm tracking-wide2 text-paper" data-id="element-1174" data-mp="sirmrlzd0xbl">
        {value || '—'}
      </p>
      {note && <span className="label-sm mt-3 block text-paper/30" data-id="element-1175" data-mp="ohfuf8p3pxzp">{note}</span>}
    </div>;
}
export function ProjectFields({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    activeProject,
    shows,
    updateProject,
    notify
  } = usePortal();
  const [draft, setDraft] = useState<Project>(activeProject);

  /* Client name, type, contact and description belong to the show as a
     whole once this episode has one — edited from Edit Show instead. */
  const show = activeProject.showId ? shows.find(item => item.id === activeProject.showId) ?? null : null;
  function edit(patch: Partial<Project>) {
    setDraft(current => ({
      ...current,
      ...patch
    }));
  }
  function save(event: React.FormEvent) {
    event.preventDefault();
    /* nextShootDate, nextShootTime and location are deliberately NOT sent —
       the database owns them. Nor is callSheet, which this form no longer
       edits. See the notes at the top. */
    updateProject({
      name: draft.name.trim() || activeProject.name,
      ...(show ? {} : {
        client: draft.client.trim() || activeProject.client,
        type: draft.type,
        description: draft.description,
        clientContact: draft.clientContact
      })
    });
    notify('Project details updated');
    onClose();
  }
  return <form onSubmit={save} className="mt-10 border border-paper/25 px-6 py-8 sm:px-8" data-id="element-1176" data-mp="1mpia122053ca">
      <h3 className="font-display text-2xl leading-none" data-id="element-1177" data-mp="1hsi0gh5y1igf">Edit Project</h3>

      <div className="mt-8" data-id="element-1178" data-mp="1crc7qjca9v6w">
        <FieldGroup label="Project" data-id="element-1179" data-mp="1cqiztf1l8zn6">
          {!show && <label className="block" data-id="element-1180" data-mp="18r1v4ntzk6j2">
              <span className="label block text-paper/40" data-id="element-1181" data-mp="18pz5ej8ega4q">Client Name</span>
              <input value={draft.client} onChange={event => edit({
            client: event.target.value
          })} className={FIELD} data-id="element-1182" data-mp="13sh1565buj32" />
            </label>}
          <label className="block" data-id="element-1183" data-mp="u2dxmc0kxmqh">
            <span className="label block text-paper/40" data-id="element-1184" data-mp="tz9meuop5qqb">Project Name</span>
            <input value={draft.name} onChange={event => edit({
            name: event.target.value
          })} className={FIELD} data-id="element-1185" data-mp="p4cig269y7ka" />
          </label>
          {!show && <label className="block" data-id="element-1186" data-mp="g3xt8rq6w8bi">
              <span className="label block text-paper/40" data-id="element-1187" data-mp="6dpj1nevifjs">Project Type</span>
              <input value={draft.type} onChange={event => edit({
            type: event.target.value
          })} placeholder="e.g. Event Coverage" className={FIELD} data-id="element-1188" data-mp="afub4hpob44b" />
            </label>}
          <ReadOnly label="Project Status" value={projectStatus(activeProject)} note="Set automatically from the deliverables" data-id="element-1189" data-mp="1fo1j6x3ioaf" />
        </FieldGroup>

        {show && <p className="label-sm mt-5 max-w-md leading-relaxed text-paper/30" data-id="element-1190" data-mp="u5wbibhha2dx">
            Client name, type, contact and description are shared across every episode of{' '}
            {show.name} — edit them from Edit Show instead.
          </p>}

        <FieldGroup label="Next shoot day" data-id="element-1191" data-mp="183b7y065fafe">
          <ReadOnly label="Date" value={activeProject.nextShootDate} data-id="element-1192" data-mp="181hykd45ic3p" />
          <ReadOnly label="Time" value={activeProject.nextShootTime} data-id="element-1193" data-mp="1cv2xq8hhdc4h" />
          <ReadOnly label="Where" value={activeProject.location} data-id="element-1194" data-mp="2xk6rptat5m62" />
        </FieldGroup>

        <p className="label-sm mt-5 max-w-md leading-relaxed text-paper/30" data-id="element-1195" data-mp="2slvi9jofddod">
          The date, time and place follow the shoot days — add and change those on the Calendar.
          Anything typed here would be overwritten the next time one of them moved.
        </p>

        {!show && <FieldGroup label="On the day" data-id="element-1196" data-mp="2elkmc289d8wc">
            <label className="block" data-id="element-1197" data-mp="2emndmb9htdcs">
              <span className="label block text-paper/40" data-id="element-1198" data-mp="2enoyt8k83g88">Client contact on set</span>
              <input value={draft.clientContact} onChange={event => edit({
            clientContact: event.target.value
          })} placeholder="e.g. Sarah Jones · Marketing Director" className={FIELD} data-id="element-1199" data-mp="29ou9dnrr2tf5" />
              <span className="label-sm mt-3 block text-paper/30" data-id="element-1200" data-mp="2v9v8gwc7jkg">
                Printed on call sheets. Who gets emailed about a new cut is set in Telling The
                Client.
              </span>
            </label>
          </FieldGroup>}

        {!show && <div className="mt-10" data-id="element-1201" data-mp="mxmmm36o1xmw">
            <span className="label block text-paper/25" data-id="element-1202" data-mp="myxnnv8q8rab">Description · studio only</span>
            <label className="mt-5 block" data-id="element-1203" data-mp="qz7vzejzrtsc">
              <span className="sr-only" data-id="element-1204" data-mp="vqpw60e6xqc9">Project Description</span>
              <textarea value={draft.description} onChange={event => edit({
            description: event.target.value
          })} rows={3} placeholder="e.g. A three-part leadership series filmed at the client's Dubai office." className={`${FIELD} resize-none`} data-id="element-1205" data-mp="vsapxnsol592" />
              <span className="label-sm mt-3 block text-paper/30" data-id="element-1206" data-mp="1eplsbh9m0esq">
                For us. The line the client reads is the outline on their page.
              </span>
            </label>
          </div>}
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-6" data-id="element-1207" data-mp="3gg6xcjay62sp">
        <button type="submit" className={BUTTON_SOLID} data-id="element-1208" data-mp="3giryt77c2gpx">
          Save Project
        </button>
        <button type="button" onClick={onClose} className={BUTTON_QUIET} data-id="element-1209" data-mp="38zagfx5trwuy">
          Cancel
        </button>
      </div>
    </form>;
}